import { getFormImportOptions } from './file-import-options.mjs'

function normalizeSourceIndex(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null
  if (typeof value === 'string' && value.trim() === '') return null

  const normalized = Number(value)
  return Number.isInteger(normalized) && normalized >= 0 ? normalized : null
}

export function extractFileImportColumn(tableData, columnIndex) {
  const normalizedColumnIndex = normalizeSourceIndex(columnIndex)
  if (!Array.isArray(tableData) || tableData.length === 0) return { ok: false }
  if (normalizedColumnIndex === null) return { ok: false }

  const headerRow = tableData[0]
  if (!Array.isArray(headerRow) || normalizedColumnIndex >= headerRow.length) return { ok: false }

  const options = []
  tableData.slice(1).forEach((row, rowIndex) => {
    if (!Array.isArray(row) || normalizedColumnIndex >= row.length) return
    options.push({
      id: rowIndex + 1,
      input: row[normalizedColumnIndex],
    })
  })

  return {
    ok: true,
    headerName: headerRow[normalizedColumnIndex],
    options,
  }
}

export function areFileImportOptionsEqual(current, next) {
  if (!Array.isArray(current) || !Array.isArray(next) || current.length !== next.length) return false
  return current.every((item, index) => (
    item?.id === next[index]?.id && Object.is(item?.input, next[index]?.input)
  ))
}

export function groupFileImportHeaders(headers) {
  const groupsByKey = new Map()
  const invalidHeaders = []

  ;(Array.isArray(headers) ? headers : [])
    .filter(header => header?.header_category === 'file_import')
    .forEach(header => {
      const dataId = header.file_import_data_id
      const tableIndex = normalizeSourceIndex(header.file_import_data_currTableIdx)
      const columnIndex = normalizeSourceIndex(header.file_import_data_select_col_idx)
      if (
        dataId === null || dataId === undefined ||
        tableIndex === null || columnIndex === null
      ) {
        invalidHeaders.push(header)
        return
      }

      const key = JSON.stringify([dataId, tableIndex])
      if (!groupsByKey.has(key)) {
        groupsByKey.set(key, { key, dataId, tableIndex, headers: [] })
      }
      groupsByKey.get(key).headers.push(header)
    })

  return {
    groups: [...groupsByKey.values()],
    invalidHeaders,
  }
}

export async function synchronizeFileImportSection({ section, loadSource } = {}) {
  const { groups, invalidHeaders } = groupFileImportHeaders(section?.section_headers)
  let changed = false
  let failed = invalidHeaders.length > 0

  if (typeof loadSource !== 'function') {
    return { changed, failed: failed || groups.length > 0 }
  }

  const results = await Promise.allSettled(groups.map(async group => ({
    group,
    tableData: await loadSource(group),
  })))

  results.forEach(result => {
    if (result.status !== 'fulfilled') {
      failed = true
      return
    }

    const { group, tableData } = result.value
    group.headers.forEach(header => {
      const syncResult = synchronizeFileImportHeader({
        header,
        sectionRows: section?.section_rows,
        tableData,
      })
      if (!syncResult.ok) failed = true
      if (syncResult.changed) changed = true
    })
  })

  return { changed, failed }
}

export function synchronizeFileImportHeader({ header, sectionRows = [], tableData } = {}) {
  if (!header || typeof header !== 'object') {
    return { ok: false, changed: false, clearedCellCount: 0 }
  }

  const extracted = extractFileImportColumn(tableData, header.file_import_data_select_col_idx)
  if (!extracted.ok) return { ok: false, changed: false, clearedCellCount: 0 }

  const changed = (
    !Object.is(header.header_name, extracted.headerName) ||
    !areFileImportOptionsEqual(header.col_dropdown_origin, extracted.options)
  )
  if (!changed) return { ok: true, changed: false, clearedCellCount: 0 }

  header.header_name = extracted.headerName
  header.col_dropdown_origin = extracted.options
  header.col_dropdown = getFormImportOptions(header, extracted.options)

  const allowedValues = extracted.options.map(option => option.input)
  let clearedCellCount = 0
  sectionRows.forEach(row => {
    if (!Array.isArray(row?.cells)) return
    row.cells.forEach(cell => {
      if (cell?.header_id !== header.id || cell.value === null) return
      if (!allowedValues.some(value => Object.is(value, cell.value))) {
        cell.value = null
        clearedCellCount += 1
      }
    })
  })

  return { ok: true, changed: true, clearedCellCount }
}
